import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import { BASE_URL } from "../../../config/api";
import RejectWarranty from "./RejectWarranty";
import ExpandableCard from "../../../Component/Common/ExpandableCard";
import SimpleTable from "../../../Component/Common/SimpleTable";
import { formatDateTime } from "../../../Component/Common/formatDateTime";
import Badge from "../../../Component/Common/Badge";
import ApproveRejectModal from "./WarrantiesClaimsView/ApproveRejectModal";
import ClaimCard from "../../../Component/Common/ClaimCard";
import axios from "axios";

/* ================= HELPERS ================= */

const formatDate = (d) => (d ? d.slice(0, 10) : "—");

const formatMoney = (n) =>
  typeof n === "number" ? `₹${n.toLocaleString()}` : "—";

const getDaysRemaining = (expiryDate) => {
  if (!expiryDate) return "—";
  const today = new Date();
  const expiry = new Date(expiryDate);
  const diff = Math.ceil(
    (expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  return diff > 0 ? `${diff} days` : "Expired";
};

const StatusBadge = ({ isApproved }) => {
  const map = {
    0: { label: "Provisioning", className: "bg-amber-100 text-amber-700" },
    1: { label: "Active", className: "bg-emerald-100 text-emerald-700" },
    2: { label: "Rejected", className: "bg-rose-100 text-rose-700" },
  };

  const status = map[isApproved] || {
    label: "Unknown",
    className: "bg-slate-200 text-slate-700",
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}
    >
      {status.label}
    </span>
  );
};


function Skeleton({ className = "" }) {
  return (
    <div className={`animate-pulse bg-slate-200 rounded-lg ${className}`} />
  );
}

function WarrantyDetailsSkeleton() {
  return (
    <div className="p-6 space-y-6 min-h-screen">

      {/* Header */}
      <div className="h-24 rounded-2xl bg-slate-200 animate-pulse" />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => (
          <Skeleton key={i} className="h-20 rounded-2xl" />
        ))}
      </div>

      {/* Detail Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1,2,3,4].map(i => (
          <div key={i} className="rounded-2xl border border-slate-200 p-6 space-y-4">
            <Skeleton className="h-6 w-40" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1,2,3,4,5,6].map(j => (
                <Skeleton key={j} className="h-4 w-full" />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Claims Table */}
      <div className="rounded-2xl border border-slate-200 p-6 space-y-3">
        <Skeleton className="h-6 w-40" />
        {[1,2,3,4,5].map(i => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>

    </div>
  );
}

const PaymentBadge = ({ paid }) => {
  return paid ? (
    <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
      Paid
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
      Unpaid
    </span>
  );
};


/* ================= COMPONENT ================= */

export default function WarrantyDetails() {
  const { id } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const [claims, setClaims] = useState([]);
  const [claimsPage, setClaimsPage] = useState(1);
  const [claimsTotalPages, setClaimsTotalPages] = useState(1);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);

  const authUserString = localStorage.getItem("authUserData");
  const authUser = authUserString ? JSON.parse(authUserString) : null;

  const LoginUserName = authUser?.name || authUser?.business_name;
  const loginUserCode = authUser?.org_code;
  const userRole = authUser?.role;

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isRetailerMobile = isMobile && (authUser?.role === 5 || authUser?.role === 6) ;

  useEffect(() => {

    const fetchDetails = async () => {

      try {

        const response =
          await axios.get(
            `${BASE_URL}warranty/get-devices`,
            {
              params: {
                id,
              },

              headers: {
                Accept:
                  "application/json",

                "Content-Type":
                  "application/json",
              },
            }
          );

        const json =
          response.data;

        // API returns paginated list
        setDetails(
          json?.data?.[0] || null
        );

      } catch (err) {

        console.error(
          "Failed to fetch details",
          err
        );

      } finally {

        setLoading(false);

      }
    };

    fetchDetails();

  }, [id]);


  useEffect(() => {
  if (!id) return;

  const fetchClaims = async () => {

    try {

      setClaimsLoading(true);

      const response =
        await axios.get(
          `${BASE_URL}warranty/claims/list`,
          {
            params: {
              id,
              page: claimsPage,
            },

            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      const json =
        response.data;

      setClaims(
        json?.data?.data || []
      );

      setClaimsTotalPages(
        json?.data?.last_page || 1
      );

    } catch (err) {

      console.error(
        "Failed to fetch claims",
        err
      );

    } finally {

      setClaimsLoading(false);

    }
  };

  fetchClaims();
}, [id, claimsPage]);

  const claimRows = claims.map((c) => ({
    raw: c,
    cells: [
      c.created_at?.slice(0, 10),
      c.claim_code || c.id,
      c.device?.product_name || "—",
      c.device?.model || "—",
      c.claim_type,
      <Badge key={c.id} label={c.status} color="blue" />,
    ],
  }));


  if (loading) return <WarrantyDetailsSkeleton />;
 if (!details) return <p>No warranty details found.</p>;


  const device = details;
  const customer = details.customer || {};
  const retailer = details.retailer || {};
  console.log("retailer details: ", retailer)

const isPaid =
  device.is_pay_later === null && device.invoice_id !== null;

  return (
    <div className=" space-y-6 min-h-screen">

      {/* ================= HEADER ================= */}
      <div
        className={`p-6 rounded-2xl text-white
          ${
            isRetailerMobile
              ? "bg-gradient-to-br from-blue-600/80 to-indigo-700/80 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.35)]"
              : "bg-blue-800 shadow"
          }
        `}
      >
        <div className="flex flex-row sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">Warranty Details</h2>
            <p className="text-sm text-slate-300">
              {device.w_code}
            </p>
          </div>
          <StatusBadge isApproved={device.is_approved} />
        </div>
      </div>

      {/* ================= STATS ================= */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          title="Warranty Price"
          value={`₹${device.product_mrp}`}
          icon="🛡️"
          isRetailerMobile={isRetailerMobile}
        />
        <StatCard
          title="Days Remaining"
          value={getDaysRemaining(device.expiry_date)}
          icon="📅"
          isRetailerMobile={isRetailerMobile}
        />
        <StatCard
          title="Available Claims"
          value={device.available_claim ?? "—"}
          icon="💰"
          isRetailerMobile={isRetailerMobile}
        />
        <StatCard
          title="Device Price"
          value={formatMoney(device.device_price)}
          icon="📱"
          isRetailerMobile={isRetailerMobile}
        />
      </div>

      {/* ================= DETAILS ================= */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* WARRANTY DETAILS */}
        <GlassCard title="Warranty Details" icon="🛡️" isRetailerMobile={isRetailerMobile}>
          <Grid>
            <LabelValue label="Plan Name" value={device.product_name} />
            <LabelValue label="Brand" value={device.brand_name} />
            <LabelValue label="Purchase Date" value={formatDateTime(device.created_at)} />
            <LabelValue label="Expiry Date" value={formatDateTime(device.expiry_date)} />
            <LabelValue label="Days Remaining" value={getDaysRemaining(device.expiry_date)} />
            <LabelValue label="Warranty Price" value={`₹${device.product_mrp}`} />
          </Grid>
        </GlassCard>

        {/* DEVICE INFO */}
        <GlassCard title="Device Information" icon="📱" isRetailerMobile={isRetailerMobile}>
          <Grid>
            <LabelValue label="Category" value={device.category_name} />
            <LabelValue label="Model" value={device.model} />
            <LabelValue label="IMEI 1" value={device.imei1} />
            <LabelValue label="IMEI 2" value={device.imei2} />
            <LabelValue label="Serial Number" value={device.serial} />
          </Grid>
        </GlassCard>

        {/* RETAILER INFO */}
        <GlassCard title="Retailer & Customer Information" icon="🏪" isRetailerMobile={isRetailerMobile}>
          <Grid>
            <LabelValue label="Business Name" value={`${retailer?.business_name} (${retailer?.company_code})`} />
            <LabelValue label="Contact Person" value={retailer?.contact_person} />
            <LabelValue label="Phone" value={retailer?.contact_phone} /> 
            <LabelValue label="Address" value={`${retailer?.address_line1 || ""}, ${retailer?.address_line2 || ""}, ${retailer?.city}, ${retailer?.state}`} />
            <LabelValue label="Customer Name" value={`${customer?.name} (${customer?.c_code})`} />
            <LabelValue
              label="Customer Address"
              value={`${customer?.address1 || ""}, ${customer?.address2 || ""}, ${customer?.city}, ${customer?.state}`}
            />
          </Grid>
        </GlassCard>

        {/* COVERAGE */}
        <GlassCard title="Dealer Payouts" icon="💰" isRetailerMobile={isRetailerMobile}>

            <div className="flex justify-end mb-3">
              <PaymentBadge paid={isPaid} />
            </div>

          <Grid>
            <LabelValue label="Device Price" value={formatMoney(device.device_price)} />
            <LabelValue label="Warranty Price" value={formatMoney(device.product_mrp)} />
            <LabelValue label="ARP Payout" value={formatMoney(device.retailer_payout)} />
            <LabelValue label="Promoter Payout" value={formatMoney(device.employee_payout)} />

            {authUser?.role === 5 && (
            <LabelValue label="Payable Amount" value={formatMoney(device.product_price)} />
            )}

            {/* <LabelValue label="Agent Payout" value={formatMoney(device.other_payout)} /> */}
          </Grid>
        </GlassCard>

        {device.is_approved === 2 && (
          <GlassCard title="Rejection Details" icon="❌" isRetailerMobile={isRetailerMobile}>
            <Grid>
              <LabelValue
                label="Reject Reason"
                value={device.reject_remark || "—"}
              />
              <LabelValue
                label="Note"
                value={device.note || "—"}
              />
              <LabelValue
                label="Rejected On"
                value={formatDateTime(device.updated_at)}
              />
            </Grid>
          </GlassCard>
        )}
      </div>

      {/* ================= CLAIM DETAILS ================= */}
      {/* <GlassCard title="Claim Details" icon="📄">

        <div className="block md:hidden">
          {claimsLoading ? (
            <RegularLoader />
          ) : claims.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">
              No claims found
            </p>
          ) : (
            claims.map((c) => (
              <ExpandableCard
                key={c.id}
                title={`Claim #${c.id}`}
                badge={{ label: c.status, color: "blue" }}
                primaryFields={[
                  { label: "Date", value: formatDateTime(c.created_at) },
                  { label: "Product", value: c.device?.product_name },
                ]}
                extraFields={[
                  { label: "Model", value: c.device?.model },
                  { label: "IMEI", value: c.device?.imei1 },
                  {
                    label: "Issues",
                    value: c.coverages?.map((x) => x.coverage_title).join(", "),
                  },
                ]}
                onView={() => {
                  setSelectedClaim(c);
                  setShowClaimModal(true);
                }}
              />
            ))
          )}

        </div>

        <div className="hidden md:block">
          {claimsLoading ? (
            <RegularLoader />
          ) : claims.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">
              No claims found
            </p>
          ) : (
            <SimpleTable
              headers={[
                "Date",
                "Claim Code",
                "Product",
                "Model",
                "Type",
                "Status",
              ]}
              rows={claimRows}

              renderActions={(row) => (
                    <button
                      onClick={() => {
                        setSelectedClaim(row);
                        setShowClaimModal(true);
                      }}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      View
                    </button>
                  )}
              showSrNo
              currentPage={claimsPage}
              totalPages={claimsTotalPages}
              onPageChange={setClaimsPage}
            />
          )}
        </div>
      </GlassCard> */}

      <GlassCard title="Claim Details" icon="📄" isRetailerMobile={isRetailerMobile}>

        {/* ================= MOBILE ================= */}
        <div className="block md:hidden">
          {claimsLoading ? (
            <div className="py-6 flex justify-center">
              <RegularLoader />
            </div>
          ) : claims.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">
              No claims found
            </p>
          ) : (
            claims.map((w) => (
              <ClaimCard
                key={w.id}
                data={{
                  ui: {
                    id: w.claim_code || w.id,
                    status: w.status || "Pending",
                    productName: w.device?.product_name || "—",
                    imei: w.device?.imei1 || "—",
                    claimDate: {
                      date: formatDateTime(w.created_at),
                      raw: w.created_at,
                    },
                    amount: w.claim_amount,
                    registeredBy: w.customer?.name || "—",
                    reasons: w.coverages?.map(c => c.coverage_title) || [],
                  },
                }}
                onView={() => {
                  setSelectedClaim(w);
                  setShowClaimModal(true);
                }}
                isRetailerMobile={isRetailerMobile}
              />
            ))
          )}
        </div>

        {/* ================= DESKTOP ================= */}
        <div className="hidden md:block">
          {claimsLoading ? (
            <div className="py-10 flex justify-center">
              <RegularLoader />
            </div>
          ) : claims.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">
              No claims found
            </p>
          ) : (
            <SimpleTable
              headers={[
                "Date",
                "Claim Code",
                "Product",
                "IMEI",
                "Claim Type",
                "Status",
              ]}
              rows={claimRows}
              showSrNo
              currentPage={claimsPage}
              totalPages={claimsTotalPages}
              onPageChange={setClaimsPage}
              renderActions={(row) => (
                <button
                  onClick={() => {
                    setSelectedClaim(row);
                    setShowClaimModal(true);
                  }}
                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  View
                </button>
              )}
            />
          )}
        </div>
      </GlassCard>

      {/* ================= REJECT WARRANTY ================= */}
      {authUser.role === 1 && device.is_approved !== 2 && (
        <div className="p-6 rounded-2xl border border-rose-200 bg-rose-50 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <span className="h-9 w-9 flex items-center justify-center rounded-full bg-rose-100 text-rose-700">
            ❌
          </span>
          <h3 className="text-lg font-semibold text-rose-700">
            Reject Warranty
          </h3>
        </div>

        <p className="text-sm text-rose-600 mb-4">
          This action is irreversible. Please verify details carefully.
        </p>

          <RejectWarranty
            warrantyId={id}
            isRejected={device.status === "Rejected"}
            onSuccess={() => window.location.reload()}
          />
      </div>
      )}

      {showClaimModal && selectedClaim && (
        
        <ApproveRejectModal
          claim={selectedClaim}
          isAdmin={false}      // 🔒 retailer view
          isCompany={false}   // 🔒 retailer view
          onClose={() => {
            setShowClaimModal(false);
            setSelectedClaim(null);
          }}
          onAssign={() => {}}
          onApprove={() => {}}
          onReject={() => {}}
        />
      )}
    </div>
  );
}

/* ================= UI COMPONENTS ================= */

function GlassCard({ title, icon, children, isRetailerMobile }) {
  return (
    <div
      className={`rounded-2xl p-4 transition-all duration-300
        ${
          isRetailerMobile
            ? "glass-card bg-white/10 backdrop-blur-2xl border border-white/30 text-slate-900 shadow-[0_20px_50px_rgba(31,38,135,0.25)]"
            : "bg-white border border-slate-200 shadow-sm hover:shadow-md"
        }
      `}
    >
      <div className="flex items-center gap-3 mb-4">
        <span
          className={`h-9 w-9 flex items-center justify-center rounded-full text-lg
            ${
              isRetailerMobile
                ? "bg-white/20 backdrop-blur-xl border border-white/30"
                : "bg-slate-100"
            }
          `}
        >
          {icon}
        </span>
        <h3 className="text-lg font-semibold">
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
      {children}
    </div>
  );
}

function LabelValue({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className="text-sm font-medium text-slate-800">
        {value || "—"}
      </p>
    </div>
  );
}

function StatCard({ title, value, icon, isRetailerMobile }) {
  return (
    <div
      className={`relative p-3 rounded-2xl transition-all
        ${
          isRetailerMobile
            ? "bg-white/10 backdrop-blur-xl border border-white/30 shadow-[0_15px_35px_rgba(31,38,135,0.25)]"
            : "bg-white border border-slate-100 shadow-sm"
        }
      `}
    >
      <div className="absolute top-2 right-2 text-xl opacity-20">
        {icon}
      </div>
      <p className="text-xs uppercase tracking-wide">
        {title}
      </p>
      <p className="mt-1 text-lg font-semibold">
        {value}
      </p>
    </div>
  );
}


// import { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import RegularLoader from "../../../Component/Loader/RegularLoader";
// import { BASE_URL } from "../../../config/api";
// import RejectWarranty from "./RejectWarranty";

// export default function WarrantyDetails() {
//   const { id } = useParams();
//   const [details, setDetails] = useState(null);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     const fetchDetails = async () => {
//       try {
//         const res = await fetch(
//           `${BASE_URL}warranty/get-customer-details?id=${id}`
//         );
//         const data = await res.json();
//         setDetails(data);
//       } catch (err) {
//         console.error("Failed to fetch details", err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchDetails();
//   }, [id]);

//   const getDaysRemaining = (expiryDate) => {
//   if (!expiryDate) return "—";

//   const today = new Date();
//   const expiry = new Date(expiryDate);

//   const diffTime = expiry.getTime() - today.getTime();
//   const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

//   return diffDays > 0 ? `${diffDays} days` : "Expired";
// };


//   if (loading) return <RegularLoader />;
//   if (!details) return <p>No details found.</p>;

//   const device = details.devices?.[0] || {};

//   return (
//     <div className="p-6 space-y-6">

//       {/* PAGE HEADER */}
//       <div className="flex justify-between items-center">
//         <h2 className="text-xl font-semibold text-slate-800">Warranty Details</h2>

//         <span className="px-4 py-1 rounded-full text-sm bg-green-100 text-green-700">
//           {device.status || "Active"}
//         </span>
//       </div>

//       {/* WARRANTY DETAILS CARD - EXACT UI MATCH */}
     

//       {/* TOP STAT CARDS */}
//       <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
//         <StatCard title="Coverage Remaining" value={device.product_price} icon="🛡️" />
//         <StatCard title="Days Remaining" value={getDaysRemaining(device.expiry_date)} icon="📅" />

//         <StatCard title="Total Claims" value={device.available_claim} icon="💰" />
//         <StatCard title="Claims Count" value="2" icon="💲" />
//       </div>

//       {/* DETAILS SECTION */}
//       <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

//          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">

//             <div className="flex items-center gap-2 mb-4">
//             <span className="text-xl">🛡️</span>
//             <h3 className="text-lg font-semibold text-slate-800">Warranty Details</h3>
//             </div>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-y-5">

//             {/* LEFT COLUMN */}
//             <div>
//                 <LabelValue 
//                 label="Warranty ID" 
//                 value={device.certificate_link?.split("/").pop()} 
//                 />
//                 <LabelValue 
//                 label="Purchase Date" 
//                 value={device.created_at?.slice(0, 10)} 
//                 />
//                 <LabelValue 
//                 label="Days Remaining" 
//                 value="36 days" 
//                 />
//             </div>

//             {/* RIGHT COLUMN */}
//             <div>
//                 <LabelValue label="Plan Name" value={device.product_name} />
//                 <LabelValue label="Expiry Date" value={device.expiry_date} />
//                 <div className="flex items-center gap-2 mt-1">
//                 <span className="text-sm font-medium text-slate-600">Status:</span>
//                 <span className="px-3 py-1 rounded-full bg-green-500 text-white text-xs">
//                     Active
//                 </span>
//                 </div>
//             </div>
//             </div>
//         </div>

//         {/* === DEVICE INFORMATION CARD === */}
//         <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">

//         {/* ICON + TITLE */}
//         <div className="flex items-center gap-2 mb-4">
//             <span className="text-xl">📱</span>
//             <h3 className="text-lg font-semibold text-slate-800">Device Information</h3>
//         </div>

//         <div className="grid grid-cols-1 md:grid-cols-2 gap-y-5">

//             {/* LEFT COLUMN */}
//             <div>
//             <LabelValue label="Device" value={device.product_name} />
//             <LabelValue label="IMEI 1" value={device.imei1} />
//             </div>

//             {/* RIGHT COLUMN */}
//             <div>
//             <LabelValue label="Model" value={device.model} />
//             <LabelValue label="IMEI 2" value={device.imei2} />
//             </div>

//         </div>
//         </div>


//         <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
//             <div className="flex items-center gap-2 mb-4">
//                 <span className="text-xl">🏪</span>
//                 <h3 className="text-lg font-semibold text-slate-800">Retailer Information</h3>
//             </div>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-y-5">

//                 {/* LEFT COLUMN */}
//                 <div>
//                 <LabelValue label="Retailer Name" value={details.retailer?.retailer_name} />
//                 <LabelValue label="Shop Name" value={details.retailer?.business_name} />
//                 </div>

//                 {/* RIGHT COLUMN */}
//                 <div>
//                 <LabelValue label="Retailer Code" value={details.retailer?.org_code} />
//                 <LabelValue label="Address" value={details.retailer?.address} />
//                 </div>
//             </div>
//         </div>

//         <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
//             <div className="flex items-center gap-2 mb-4">
//                 <span className="text-xl">💰</span>
//                 <h3 className="text-lg font-semibold text-slate-800">Coverage Details</h3>
//             </div>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-y-5">

//                 {/* LEFT COLUMN */}
//                 <div>
//                 <LabelValue label="Original Coverage" value="₹2,500" />
//                 <LabelValue label="Monthly Reduction" value="₹45" />
//                 </div>

//                 {/* RIGHT COLUMN */}
//                 <div>
//                 <LabelValue label="Total Claims" value="₹449.99" />
//                 <LabelValue label="Current Coverage" value="₹1,060.01" />
//                 </div>
//             </div>
//         </div>


//       </div>

//       {/* CLAIM TABLE */}
//       <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
//         <h3 className="font-semibold mb-3 text-slate-700">Claim Details</h3>

//         <table className="w-full text-sm">
//           <thead>
//             <tr className="text-slate-500 border-b bg-slate-50">
//               <th className="py-2 text-left">Sr.</th>
//               <th className="py-2 text-left">Purchase Date</th>
//               <th className="py-2 text-left">Warranty ID</th>
//               <th className="py-2 text-left">Retailer Code</th>
//               <th className="py-2 text-left">Customer</th>
//               <th className="py-2 text-left">Device</th>
//               <th className="py-2 text-left">Claims Processed</th>
//               <th className="py-2 text-left">Expiry Date</th>
//               <th className="py-2 text-left">Status</th>
//             </tr>
//           </thead>

//           <tbody>
//             <tr className="border-b hover:bg-slate-50">
//               <td className="py-2">1</td>
//               <td>{device.created_at?.slice(0, 10)}</td>
//               <td>{device.certificate_link?.split("/").pop()}</td>
//               <td>{details.retailer?.org_code}</td>
//               <td>{details.name}</td>
//               <td>{device.product_name}</td>
//               <td>2</td>
//               <td>{device.expiry_date}</td>
//               <td>
//                 <span className="px-3 py-1 text-xs rounded-full bg-green-100 text-green-700">
//                   Active
//                 </span>
//               </td>
//             </tr>
//           </tbody>
//         </table>
//       </div>

//       <div className="bg-white p-6 rounded-xl border border-red-200 shadow-sm">
//           <div className="flex items-center gap-2 mb-4">
//             <span className="text-xl">❌</span>
//             <h3 className="text-lg font-semibold text-red-700">
//               Reject Warranty
//             </h3>
//           </div>

//           <RejectWarranty
//             warrantyId={id}
//             isRejected={device.status === 2}
//             onSuccess={() => window.location.reload()}
//           />
//         </div>
//     </div>
//   );
// }

// /* === REUSABLE COMPONENTS === */

// function LabelValue({ label, value }) {
//   return (
//     <div className="mb-3">
//       <p className="text-sm font-medium text-slate-600">{label}</p>
//       <p className="text-md text-slate-800">{value || "---"}</p>
//     </div>
//   );
// }


// function StatCard({ title, value, icon }) {
//   return (
//     <div className="p-4 bg-white rounded-xl shadow-sm border border-slate-100">
//       <div className="text-2xl">{icon}</div>
//       <p className="text-xs text-slate-500">{title}</p>
//       <p className="text-md font-semibold text-slate-800">{value}</p>
//     </div>
//   );
// }

// function Card({ title, children }) {
//   return (
//     <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-100">
//       <h3 className="font-semibold text-slate-700 mb-3">{title}</h3>
//       <div className="space-y-1">{children}</div>
//     </div>
//   );
// }

// function Info({ label, value }) {
//   return (
//     <p className="text-sm">
//       <span className="font-medium text-slate-600">{label}:</span>{" "}
//       <span className="text-slate-700">{value || "---"}</span>
//     </p>
//   );
// }
